import { STORAGE_KEYS } from "@/lib/storage-keys";
import { assertPrfSession, prfSessionGeneration } from "./prf-session";

export interface VaultCredential {
	credentialId: string;
	prfSalt: string;
	keyId: string;
	encryptedBackup: string;
	deviceName?: string | null;
}
export interface PRFCatalog {
	credentials: VaultCredential[];
	vault: { keyId: string; encryptedBackup: string } | null;
}
export type PRFStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export function readPRFCatalog(
	storage: PRFStorage = localStorage
): PRFCatalog | null {
	try {
		const value = JSON.parse(
			storage.getItem(STORAGE_KEYS.PRF_VAULT_CATALOG) ?? "null"
		);
		return parseCatalog(value);
	} catch {
		return null;
	}
}
function parseCatalog(value: unknown): PRFCatalog | null {
	if (!value || typeof value !== "object") {
		return null;
	}
	const c = value as PRFCatalog;
	if (
		!(
			Array.isArray(c.credentials) &&
			(c.vault === null ||
				(typeof c.vault?.keyId === "string" &&
					typeof c.vault.encryptedBackup === "string"))
		)
	) {
		return null;
	}
	if (
		!c.credentials.every(
			(row) =>
				row &&
				typeof row.credentialId === "string" &&
				typeof row.prfSalt === "string" &&
				typeof row.keyId === "string" &&
				typeof row.encryptedBackup === "string"
		)
	) {
		return null;
	}
	return c;
}
export function storePRFCatalog(
	catalog: PRFCatalog,
	storage: PRFStorage = localStorage
): void {
	storage.setItem(STORAGE_KEYS.PRF_VAULT_CATALOG, JSON.stringify(catalog));
	// The user approved resetting obsolete single-passkey seals.
	for (const key of [
		STORAGE_KEYS.PRF_ENCRYPTED_BACKUP,
		STORAGE_KEYS.PRF_CREDENTIAL_ID,
		STORAGE_KEYS.PRF_SALT,
		STORAGE_KEYS.PRF_ENCRYPTED_ACTIVE_ACCOUNT,
		STORAGE_KEYS.PRF_SERVER_SYNC,
	]) {
		storage.removeItem(key);
	}
	if (typeof window !== "undefined") {
		window.dispatchEvent(new Event("prf-credential-hydrated"));
	}
}
export async function fetchPRFCatalog(): Promise<PRFCatalog> {
	const res = await fetch("/api/webauthn/prf/credential", {
		credentials: "include",
		cache: "no-store",
	});
	if (!res.ok) {
		throw new Error("Sign in to load your backup unlock options.");
	}
	const catalog = parseCatalog(await res.json());
	if (!catalog) {
		throw new Error("Backup unlock settings could not be loaded.");
	}
	return catalog;
}
export async function hydratePRFCredentialFromServer(
	_options: { skipIfPresent?: boolean } = {}
): Promise<boolean> {
	if (typeof window === "undefined") {
		return false;
	}
	const version = prfSessionGeneration();
	const before = localStorage.getItem(STORAGE_KEYS.PRF_VAULT_CATALOG);
	try {
		const catalog = await fetchPRFCatalog();
		assertPrfSession(version);
		if (localStorage.getItem(STORAGE_KEYS.PRF_VAULT_CATALOG) !== before) {
			return false;
		}
		storePRFCatalog(catalog);
		return true;
	} catch {
		return false;
	}
}
export async function ciphertextHash(value: string): Promise<string> {
	const bytes = await crypto.subtle.digest(
		"SHA-256",
		new TextEncoder().encode(value)
	);
	return Array.from(new Uint8Array(bytes), (b) =>
		b.toString(16).padStart(2, "0")
	).join("");
}
